/**
 * Map server `Attribute`s onto the presentational `GlobalAttribute` catalog
 * (`globalLib`) the Space editor references.
 *
 * The presentational model keys a global attribute by an opaque `key`; the
 * backend has no such field, so we use the machine `name` as the key and the
 * `label` as the display name. This must stay consistent with how
 * `apiSpaceToVaultSpace` correlates a space's attributes back to catalog
 * entries (it matches on `name`/`label`).
 */
import { dataTypeToValueType, slugToDomainId, valueTypeToDataType } from "../Space/mappers";
import type {
  GlobalAttrFlavor,
  GlobalAttribute,
  PromotionLabelConflict,
  PromotionPreflightSummary,
  SpaceLocalAttribute,
} from "#common/types/vault";
import type {
  AttributeOptionInput,
  AttributePromotionPreflightQuery,
  AttributeValueOptionPatchInput,
  CreateGlobalAttributeInput,
  QualityContract,
  SpaceLocalAttributesSearchQuery,
  UpdateAttributeInput,
  UpdateSpaceLocalAttributeInput,
} from "#graphql/supergraphTypes";
import type { Attribute } from "./hooks/useAttributes";

/** Backend `Attribute.kind` values that denote a curated global attribute. */
export const isGlobalAttributeKind = (kind: string): boolean =>
  kind === "global_system" || kind === "global_steward";

/**
 * `global_system` = engineering-maintained/locked -> `eng`; `global_steward`
 * = steward-curated -> `user`. Mirrors the vault `GlobalAttrFlavor` semantics.
 */
const flavorForKind = (kind: string): GlobalAttrFlavor =>
  kind === "global_steward" ? "user" : "eng";

/**
 * The attribute's fixed value set in `displayOrder`, or `null` when it has none
 * (non-select types, and reference types validated against a source of truth).
 * Raw option `value`s — not display labels — because the same list seeds the
 * Documents-tab set filters, which compare against the raw `attributeValues`
 * a document carries.
 */
const optionsToValues = (attr: Attribute): string[] | null =>
  attr.options.length
    ? [...attr.options].sort((a, b) => a.displayOrder - b.displayOrder).map((o) => o.value)
    : null;

/**
 * Backend option id for each option, keyed by its display value — lets a
 * picked select value resolve back to the id `valueIds` requires (DV-466/
 * DV-544). `undefined` when the attribute has no options.
 */
const optionIdsByValue = (attr: Attribute): Record<string, string> | undefined =>
  attr.options.length
    ? Object.fromEntries(attr.options.map((o) => [o.value, o.id]))
    : undefined;

/** One node of the `spaceLocalAttributesSearch` connection. */
export type SpaceLocalAttributeNode =
  SpaceLocalAttributesSearchQuery["spaceLocalAttributesSearch"]["nodes"][number];

/**
 * A space-local attribute from the cross-space search, with its owning space
 * flattened alongside it. The nested `local` matches what `apiSpaceToVaultSpace`
 * puts in `Space.locals`, so the edit/promote modals take it unchanged.
 */
export type VaultSpaceLocalAttribute = {
  spaceId: string;
  spaceName: string;
  /** Vault domain id, not the backend slug. */
  domain: string;
  local: SpaceLocalAttribute;
};

/** Map one `spaceLocalAttributesSearch` node onto its presentational shape. */
export const apiSpaceLocalAttributeToVaultLocal = (
  node: SpaceLocalAttributeNode,
): VaultSpaceLocalAttribute => {
  const attr = node.attribute;
  return {
    spaceId: node.space.id,
    spaceName: node.space.name,
    domain: slugToDomainId(node.space.domain),
    local: {
      id: attr.id,
      // Persisted machine slug (backend `name`) vs display `label`.
      slug: attr.name,
      name: attr.label || attr.name,
      dataType: valueTypeToDataType(attr.valueType),
      // "guaranteed" = Required, "ingested" = Optional.
      required: node.qualityContract === "guaranteed",
      default: "",
      options: optionsToValues(attr) ?? [],
      optionIdsByValue: optionIdsByValue(attr),
    },
  };
};

/**
 * Invert catalog `optionIdsByValue` maps (display value → option id) into
 * option id → display value. Fallback for the document read mapper when a
 * node's nested `attribute.options` are missing; labels come from the
 * already-fetched GlobalAttributes / Spaces catalogs.
 */
export const optionValueByIdFromCatalog = (
  globalLib: readonly Pick<GlobalAttribute, "optionIdsByValue">[],
  locals: readonly Pick<SpaceLocalAttribute, "optionIdsByValue">[] = [],
): Record<string, string> => {
  const map: Record<string, string> = {};
  const merge = (idsByValue?: Record<string, string>) => {
    if (!idsByValue) return;
    Object.entries(idsByValue).forEach(([value, id]) => {
      map[id] = value;
    });
  };
  globalLib.forEach((a) => merge(a.optionIdsByValue));
  locals.forEach((l) => merge(l.optionIdsByValue));
  return map;
};

/** Map one server global attribute onto a presentational `GlobalAttribute`. */
export const apiAttributeToGlobalAttribute = (attr: Attribute): GlobalAttribute => ({
  key: attr.name,
  name: attr.label,
  flavor: flavorForKind(attr.kind),
  dataType: valueTypeToDataType(attr.valueType),
  captures: attr.description ?? "",
  validates: attr.referenceTarget ?? null,
  values: optionsToValues(attr),
  optionIdsByValue: optionIdsByValue(attr),
});

/** The non-null payload of the `attributePromotionPreflight` query. */
export type ApiPromotionPreflight = NonNullable<
  AttributePromotionPreflightQuery["attributePromotionPreflight"]
>;

/**
 * Map the `attributePromotionPreflight` payload onto the presentational
 * `PromotionPreflightSummary` (DV-499). Straight field mapping; the generated
 * string enums carry the same values as the vault literal unions.
 */
export const apiPreflightToSummary = (
  payload: ApiPromotionPreflight,
): PromotionPreflightSummary => ({
  promotable: payload.promotable,
  blockers: payload.blockers.map((b) => b),
  conflicts: payload.labelConflicts.map((c): PromotionLabelConflict => ({
    attributeId: c.attributeId,
    kind: c.kind,
    label: c.label,
    spaces: c.spaces.map((s) => ({ id: s.id, name: s.name, domain: s.domain })),
  })),
});

const SELECT_VALUE_TYPES = new Set(["single_select", "multi_select"]);

const valuesToOptionInputs = (values: string[] | null): AttributeOptionInput[] =>
  (values ?? []).map((value) => ({ value }));

/**
 * Map a presentational `GlobalAttribute` onto a `CreateGlobalAttributeInput`
 * (the inline "create a new global" path). Consistent with the read mapper:
 * the vault `key` (machine name) -> backend `name`, the vault `name` (display)
 * -> backend `label`. The backend also requires a distinct `slug`, which is
 * the same already-slugified `key` (see `GlobalAttrModal`'s `slugify(name)`
 * in DocumentTypeSettings). `options` are only sent for select types;
 * `referenceTarget` only for reference types. `kind` is forced to
 * `global_steward` server-side, so only user-created (`flavor: "user"`)
 * globals should be created here.
 */
export const globalAttributeToCreateInput = (
  attr: GlobalAttribute,
): CreateGlobalAttributeInput => {
  const valueType = dataTypeToValueType(attr.dataType);
  return {
    name: attr.key,
    slug: attr.key,
    label: attr.name,
    valueType,
    ...(attr.captures ? { description: attr.captures } : {}),
    ...(valueType === "reference" && attr.validates ? { referenceTarget: attr.validates } : {}),
    ...(SELECT_VALUE_TYPES.has(valueType)
      ? { options: valuesToOptionInputs(attr.values) }
      : {}),
  };
};

/**
 * `values` is `null` on every non-select type, so the patch says on its own
 * whether options apply — the backend refuses `addOptions` on a non-select.
 */
const patchHasValueSet = (
  patch: Partial<GlobalAttribute>,
): patch is Partial<GlobalAttribute> & { values: string[] } =>
  patch.values !== undefined && patch.values !== null;

/**
 * The patch's values that `saved` does not already hold. Diffing against the
 * catalog rather than the editor's copy of it keeps a value some other admin
 * added in the meantime out of the request.
 */
const addedValues = (next: string[], saved: readonly string[] | null): string[] => {
  const held = new Set(saved ?? []);
  return next.filter((value) => !held.has(value));
};

/**
 * Map an edit-patch onto `UpdateAttributeInput`. `valueType` /
 * `referenceTarget` stay immutable post-creation, so they are never sent. NOTE:
 * the backend has no updatable display-`label` field, so a change to the vault
 * display `name` maps to the backend `name` (identifier).
 *
 * A picklist is append-only server-side, so only values `saved` lacks go up, as
 * `addOptions`. An edit that adds none omits the field entirely.
 */
export const globalAttrPatchToUpdateInput = (
  patch: Partial<GlobalAttribute>,
  saved: readonly string[] | null,
): UpdateAttributeInput => {
  const added = patchHasValueSet(patch) ? addedValues(patch.values, saved) : [];
  return {
    ...(patch.name !== undefined ? { name: patch.name } : {}),
    ...(patch.captures !== undefined ? { description: patch.captures } : {}),
    ...(added.length ? { addOptions: valuesToOptionInputs(added) } : {}),
  };
};

/**
 * The saved state of a space-local attribute an edit patch is diffed against —
 * the attribute as it stood when the editor opened.
 */
export type SavedSpaceLocalAttribute = Pick<
  SpaceLocalAttribute,
  "name" | "options" | "optionIdsByValue" | "required" | "slug"
>;

/** "guaranteed" = Required, "ingested" = Optional — the mapping `apiSpaceLocalAttributeToVaultLocal` reads. */
const qualityContractForRequired = (required: boolean): QualityContract =>
  required ? "guaranteed" : "ingested";

const sameOrderedValues = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * The desired full option set as id-stable patches, in display order.
 *
 * Built as **`saved` order first, then this save's genuinely-new values** — not
 * as the submitted list with anything missing re-appended. Two reasons:
 *  - The backend derives `display_order` from array position, so re-appending a
 *    value at the tail *renumbers* it. Rebuilding from `saved` keeps every
 *    option the attribute already holds exactly where the server has it, which
 *    matters once `saved` is the live read: an option another admin added must
 *    not be pushed behind this admin's addition.
 *  - Omission is how this input asks for a REMOVAL, and nothing here is allowed
 *    to delete a value: the editor gives saved rows no remove control, and a
 *    stale view must not either.
 *
 * Every retained option carries its backend id, so it is patched where it
 * stands and the `valueIds` documents store keep resolving. A value with no
 * known id is a new option. Reordering the existing set is therefore not
 * expressible here, which matches an editor that cannot reorder it.
 */
const spaceLocalOptionPatches = (
  next: readonly string[],
  saved: SavedSpaceLocalAttribute,
): AttributeValueOptionPatchInput[] => {
  const idsByValue = saved.optionIdsByValue ?? {};
  const savedOrder = saved.options ?? [];
  const submitted = next.map((value) => value.trim()).filter(Boolean);
  const added = submitted.filter(
    (value, index) => !savedOrder.includes(value) && submitted.indexOf(value) === index,
  );
  return [...savedOrder, ...added].map((value) => ({
    value,
    ...(idsByValue[value] ? { id: idsByValue[value] } : {}),
  }));
};

/**
 * Map a space-local edit patch onto `UpdateSpaceLocalAttributeInput` (DV-674).
 *
 * Every field is **diffed against `saved`**, not merely against "present in the
 * patch". The editor submits its whole form state — name, requirement and the
 * full value set on every save — so a patch says nothing about what the admin
 * actually touched. Sending a field back unchanged is not free: the backend
 * names it in the `audit_log` row's changed-field list, so reopening a modal and
 * pressing Save with no edits would forge an audit trail of a change that never
 * happened. Diffing here also lets the caller's "id alone means nothing changed"
 * guard skip the mutation entirely.
 *
 * `slug` / `dataType` are immutable post-creation and never sent.
 */
export const spaceLocalPatchToUpdateInput = (
  attributeId: string,
  patch: Partial<SpaceLocalAttribute>,
  saved: SavedSpaceLocalAttribute,
): UpdateSpaceLocalAttributeInput => {
  const options =
    patch.options !== undefined ? spaceLocalOptionPatches(patch.options, saved) : [];
  // The patches lead with `saved` order, so they differ from it exactly when
  // this save adds a value — the only value-set edit the editor can express.
  const optionsChanged =
    options.length > 0 &&
    !sameOrderedValues(
      options.map((option) => option.value),
      saved.options ?? [],
    );
  const requiredChanged = patch.required !== undefined && !!patch.required !== !!saved.required;
  return {
    id: attributeId,
    ...(patch.name !== undefined && patch.name !== saved.name ? { label: patch.name } : {}),
    ...(optionsChanged ? { options } : {}),
    ...(requiredChanged
      ? { qualityContract: qualityContractForRequired(!!patch.required) }
      : {}),
  };
};
